/**
 * VersionHistoryDrawer — right-side Drawer listing every version for the
 * selected config key (GET /config/:key/history).
 *
 * The history endpoint's SELECT omits prompt_body (per the build brief) —
 * only the currently active row (already fetched by the parent via
 * GET /config, passed down as `activeRow`) carries the full prompt text. So
 * only the active version gets a body preview here; older versions show
 * metadata only, with a caption pointing at Restore as the way to see/use an
 * old body again.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Drawer, Typography, IconButton, List, ListItem, Divider, Chip, Alert, Button, Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import { quillApi, ReasoningConfigKey, ReasoningConfigRow } from '../../../services/quillAdminService';
import { describeReasoningError } from './reasoningManifest';
import QuillActionConfirm from '../common/QuillActionConfirm';

export interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  configKey: ReasoningConfigKey;
  displayName: string;
  activeRow: ReasoningConfigRow | null;
  /** Called after a successful restore so the parent can refresh GET /config (new active row, new buffer). */
  onRestored: () => void;
}

const VersionHistoryDrawer: React.FC<VersionHistoryDrawerProps> = ({
  open,
  onClose,
  configKey,
  displayName,
  activeRow,
  onRestored,
}) => {
  const [history, setHistory] = useState<ReasoningConfigRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    quillApi.reasoning
      .getConfigHistory(configKey)
      .then((rows) => setHistory(rows))
      .catch((e) => setError(describeReasoningError(e)))
      .finally(() => setLoading(false));
  }, [configKey]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const sorted = history ? [...history].sort((a, b) => b.version - a.version) : [];
  const maxVersion = sorted.length ? sorted[0].version : 0;

  const handleRestoreConfirm = () => {
    if (restoreTarget == null) return;
    setRestoring(true);
    quillApi.reasoning
      .restoreConfig(configKey, restoreTarget)
      .then(() => {
        setRestoreTarget(null);
        load();
        onRestored();
      })
      .catch((e) => setError(describeReasoningError(e)))
      .finally(() => setRestoring(false));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '90vw', sm: 440 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: typography.fonts.display, fontWeight: 700, fontSize: '1.1rem' }}>
              Version History
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{displayName}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close version history">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <Box>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={96} sx={{ mb: 1.5, borderRadius: '10px' }} />
              ))}
            </Box>
          )}

          {!loading && error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={load}>
                  Retry
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {!loading && !error && sorted.length === 0 && (
            <EmptyState icon="history" title="No versions yet" description="No versions have been saved for this key." />
          )}

          {!loading && !error && sorted.length > 0 && (
            <List disablePadding>
              {sorted.map((row, idx) => (
                <React.Fragment key={row.version}>
                  <ListItem disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 700, fontFamily: typography.fonts.mono }}>v{row.version}</Typography>
                      {row.is_active && (
                        <Chip
                          label="Active"
                          size="small"
                          sx={{ backgroundColor: `${colors.deepTeal[500]}20`, color: colors.deepTeal[500], fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                      {row.model} · temp {row.temperature} · max_tokens {row.max_tokens}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {row.edited_by_admin_id || 'unknown admin'} ·{' '}
                      {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                    </Typography>
                    {row.edit_note && (
                      <Typography sx={{ fontSize: '0.8rem', mt: 0.5, fontStyle: 'italic', color: colors.gray[800] }}>
                        &ldquo;{row.edit_note}&rdquo;
                      </Typography>
                    )}

                    {row.is_active ? (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.25,
                          borderRadius: '8px',
                          backgroundColor: colors.softCream[200],
                          fontFamily: typography.fonts.mono,
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          maxHeight: 160,
                          overflowY: 'auto',
                        }}
                      >
                        {activeRow?.prompt_body || '(preview unavailable)'}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'text.disabled' }}>
                          body stored server-side — restore to make it active
                        </Typography>
                        <Button size="small" startIcon={<RestoreIcon />} onClick={() => setRestoreTarget(row.version)} sx={{ textTransform: 'none' }}>
                          Restore
                        </Button>
                      </Box>
                    )}
                  </ListItem>
                  {idx < sorted.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Box>

      <QuillActionConfirm
        open={restoreTarget != null}
        title={`Restore v${restoreTarget ?? ''}`}
        message={`Copies v${restoreTarget} to a new version v${maxVersion + 1} and activates it (production).`}
        confirmLabel="Restore"
        loading={restoring}
        onConfirm={handleRestoreConfirm}
        onCancel={() => setRestoreTarget(null)}
      />
    </Drawer>
  );
};

export default VersionHistoryDrawer;
